import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { Button, Logo } from '@waago/ui'
import { useAuth } from '@waago/api'
import illustrationTransport from '../../assets/onboarding-transport.png'

/**
 * AC-001 welcome carousel — Figma frame `4594:2178`.
 *
 * Four slides sell the four activities before asking for a phone number. The
 * frame draws slide 2; the remaining copy comes from spec §1 (transport),
 * §2 (livraison), §3 (location d’engins) and §4 (mise à disposition), each of
 * which the app must explain before a first-time user commits.
 */
type Slide = { title: string; body: string }

const SLIDES: Slide[] = [
  {
    title: 'Vous avez besoin de transport ?',
    body: 'Utiliser notre application pour faciliter vos déplacements en toute sécurité.',
  },
  {
    title: 'Un colis à envoyer ?',
    body: 'Faites livrer vos colis partout au Bénin et suivez-les en temps réel.',
  },
  {
    title: 'Un chantier à équiper ?',
    body: 'Louez engins et camions auprès de prestataires vérifiés, à la journée ou au mois.',
  },
  {
    title: 'Besoin d’un chauffeur ?',
    body: 'Mettez un chauffeur professionnel à disposition, pour une course ou une mission.',
  },
]

export default function WelcomePage() {
  const navigate = useNavigate()
  const { state } = useAuth()
  const [index, setIndex] = useState(0)

  if (state.status === 'authenticated') return <Navigate to="/" replace />

  const slide = SLIDES[index]!

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-[420px] flex-col px-8 text-white"
      style={{ backgroundImage: 'var(--gradient-welcome)' }}
    >
      <main id="main" className="flex flex-1 flex-col pt-[119px]">
        <img
          src={illustrationTransport}
          alt=""
          width={235}
          height={175}
          className="mx-auto h-[175px] w-[235px] object-contain"
        />

        <div className="mt-14 flex flex-col gap-3.5">
          {/* aria-live so a screen reader hears the slide change, since the
              dots below are decorative. */}
          <h1 className="text-lg leading-[22px]" aria-live="polite">
            {slide.title}
          </h1>
          <p className="text-sm leading-[17px]">{slide.body}</p>
        </div>

        <div className="mt-14 flex justify-center gap-3" role="tablist" aria-label="Diapositives">
          {SLIDES.map((s, i) => (
            <button
              key={s.title}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Diapositive ${i + 1} sur ${SLIDES.length}`}
              onClick={() => setIndex(i)}
              className="py-3"
            >
              <span
                className={`block h-1 w-4 rounded-full ${i === index ? 'bg-white' : 'bg-white/45'}`}
              />
            </button>
          ))}
        </div>
      </main>

      <div className="flex flex-col gap-6 pb-11">
        <div className="flex flex-col gap-3">
          <p className="text-center text-lg font-bold">Bienvenu sur</p>
          <Logo tone="white" className="mx-auto h-[61px] w-[185px]" />
        </div>

        <div className="flex flex-col gap-3">
          <Button block size="lg" onClick={() => void navigate('/connexion')}>
            Se connecter
          </Button>
          <Button
            block
            size="lg"
            className="bg-brand-50 text-brand-600 hover:bg-white"
            onClick={() => void navigate('/inscription')}
          >
            S’inscrire
          </Button>
        </div>
      </div>
    </div>
  )
}
